<template>
  <div class="row px-3">
    <div class="col-12 mb-4">
			<span class="back-btn" @click="$emit('setCurrentTab','PaymentDetailsForm')">
				<font-awesome-icon icon="arrow-left" size="sm" />
				Back
			</span>
    </div>

    <div class="col-12">
      <p>
        Please preview the details you've provided and scroll down to submit.
      </p>
      <div class="title-text font-weight-bold">
        Invoice details
      </div>
      <div class="row my-2">
        <div class="col-md-6 col-12 mt-3">
          <div class="form-text">Purchase order (PO) number</div>
          <div>{{filteredFormData.poNumber}}</div>
        </div>
        <div class="col-md-6 col-12 mt-3">
          <div class="form-text">Company code and name</div>
          <div>{{filteredFormData.companyCode}} - {{filteredFormData.companyCodeText}}</div>
        </div>
        <div class="col-md-6 col-12 mt-3">
          <div class="form-text">One time supplier ID</div>
          <div>{{filteredFormData.supplierNumber}}</div>
        </div>
        <div class="col-md-6 col-12 mt-3">
          <div class="form-text">One time supplier name</div>
          <div>{{fullSupplierName}}</div>
        </div>
        <div class="col-md-6 col-12 mt-3">
          <div class="form-text">Payment request amount <span class="font-italic">(including tax/VAT, if any)</span></div>
          <div>{{filteredFormData.currency}} {{filteredFormData.invoicedAmount}}</div>
        </div>
        <div class="col-md-6 col-12 mt-3">
          <div class="form-text">Invoice date</div>
          <div>{{filteredFormData.invoiceDate}}</div>
        </div>
        <div class="col-md-6 col-12 mt-3">
          <div class="form-text">Invoice reference number</div>
          <div>{{filteredFormData.invoiceNumber}}</div>
        </div>
        <div class="col-md-6 col-12 mt-3">
          <div class="form-text">Payment due date</div>
          <div>{{filteredFormData.paymentDueDate}}</div>
        </div>
        <div class="col-md-6 col-12 mt-3">
          <div class="form-text">Business reason for advance payment request</div>
          <div class="wrap-text">{{filteredFormData.businessReason}}</div>
        </div>
        <div class="col-md-6 col-12 mt-3" v-if="filteredFormData.specialInstructions">
          <div class="form-text">Special instructions</div>
          <div class="wrap-text">{{filteredFormData.specialInstructions}}</div>
        </div>
      </div>

      <preview-form-payment-details :filteredFormData="filteredFormData" />

      <preview-attachments :filteredFormData="filteredFormData" :viewAttachment="true" />

      <div class="col-12 px-xl-0 pt-5 noprint">
        <div class="d-flex flex-column align-items-center justify-content-xl-end flex-xl-row">
          <button class="btn gray-btn flat-btn close-btn mb-2 mb-xl-0 mr-xl-2" @click="navigateToHome" :disabled="isLoading">
            Cancel
          </button>
          <button class="btn submit-btn flat-btn mt-2 mt-xl-0 ml-xl-2"
                  @click="submitAdvPOBasedPaymentForm(filteredFormData)" :disabled="isLoading">
            Submit
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import cloneDeep from "lodash.clonedeep"
import PreviewFormPaymentDetails from "@/components/Payments/PaymentDetailsForm/PreviewFormPaymentDetails";
import api from "@/api";
import { mapMutations } from "vuex"
import PaymentFormsMixin from "@/mixins/PaymentFormsMixin";
import CONSTANTS from "@/store/constants";
import PreviewAttachments from "@/components/Payments/PreviewAttachments";

export default {
  name: "PreviewAdvancePOBasedPayment",
  props: {
    formData: String
  },
  mixins: [PaymentFormsMixin],
  components: {PreviewFormPaymentDetails, PreviewAttachments},
  data() {
    return {
      componentKey: 0,
      filteredFormData: {}
    }
  },
  mounted() {
    this.filterDataForPreviewAndSubmit();
  },
  computed: {
    fullSupplierName() {
      return this.filteredFormData.supplierNameContinued
          ? this.filteredFormData.supplierName + ' ' + this.filteredFormData.supplierNameContinued
          : this.filteredFormData.supplierName
    }
  },
  methods: {
    ...mapMutations(["setIsLoading","setFormattedInvoices"]),
    filterDataForPreviewAndSubmit() {
      this.filteredFormData = cloneDeep(this.$store.getters[this.formData])
      this.filteredFormData.currency = this.getTextValue(this.adminDataList.currencyList, this.filteredFormData.currency)
      this.filteredFormData.paymentType = this.getTextValue(this.adminDataList.paymentTypeOptions, this.filteredFormData.paymentType)

      //set requesterEmail to current user email
      this.filteredFormData.requesterEmail = this.currentUser.email

      this.componentKey++
    },
    async submitAdvPOBasedPaymentForm(formData) {
      formData = this.removeAttachmentFileSizes(formData)
      this.setIsLoading(true)
      this.setIsFormDataLoaded(false)
      this.setFormValidationErrors([])

      try {
        const resp = await api.submitAdvancePOPaymentFormData(formData)
        let returnMessages = resp.data.data.submitAdvancePayment.webformResponse.returnMessages
        if(returnMessages && returnMessages.length > 0) {
          let statusId = returnMessages[0].messageId
          let webformSubmissionStatus = await this.getSupplierSubmissionStatusById(statusId)
          if (webformSubmissionStatus && webformSubmissionStatus.status === CONSTANTS.WEBFORM_SUBMISSION_STATUS.SUBMITTED) {
            let userMessages = this.buildMessageString(webformSubmissionStatus)
            this.launchSuccessModal(userMessages, 'Supplier Central - Advance PO Payment form submit success')
            this.setIsInvoicesAndDraftsCached(true)
          } else if (webformSubmissionStatus && webformSubmissionStatus.status === CONSTANTS.WEBFORM_SUBMISSION_STATUS.FAILED) {
            this.setFormValidationErrors(webformSubmissionStatus.responseMessages)
            this.setIsFormDataLoaded(true)
            this.validateAndSetCurrentTab('AdvancePOBasedPaymentQuestionnaire')
            //Reset invoices dashboard to re-fetch tracker data
            this.setFormattedInvoices([])
          } else {
            this.launchFailedModal(CONSTANTS.ERROR_MESSAGE, 'Supplier Central - Advance PO Payment form api error')
          }
        }
      } catch {
        this.launchFailedModal(CONSTANTS.ERROR_MESSAGE, 'Supplier Central - Advance PO Payment form api error')
      } finally {
        this.setIsLoading(false)
      }

    }
  }
}
</script>

<style>
.form-text {
  font-weight: bold;
  margin-top: 10px;
}

.title-text {
  padding: 0;
  margin: 8px 0;
}
</style>